import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Box, Typography, CircularProgress, Button } from "@mui/material";

export default function TermCondition() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const settings = useSelector(state => state.settingsdata.settings);

  const termsUrl: string | null | undefined = settings?.termsOfUse;
  const [iframeLoading, setIframeLoading] = useState(true);
  const [iframeError, setIframeError] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showViewer = !!termsUrl && !iframeError;

  useEffect(() => {
    if (!showViewer) return;
    setIframeLoading(true);
    timeoutRef.current = setTimeout(() => {
      setIframeLoading(current => {
        if (current) setIframeError(true);
        return false;
      });
    }, 15000);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [termsUrl, showViewer]);

  const handleLoaded = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIframeLoading(false);
  };

  const handleError = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIframeLoading(false);
    setIframeError(true);
  };

  if (showViewer) {
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          width: '100vw',
          height: '100dvh',
          bgcolor: 'background.paper',
          overflow: 'hidden',
        }}
      >
        {iframeLoading && (
          <Box
            sx={{
              inset: 0,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 2,
              zIndex: 1,
              bgcolor: 'background.paper',
            }}
          >
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              {t('loading') || 'Carregando...'}
            </Typography>
          </Box>
        )}

        <iframe
          src={termsUrl!}
          title="Termos de Uso"
          style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
          onLoad={handleLoaded}
          onError={handleError}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        minHeight: '100vh',
        p: { xs: 2, sm: 3, md: 4 },
        direction: isRTL ? 'rtl' : 'ltr',
      }}
    >
      <Typography
        variant="body1"
        paragraph
        sx={{ textAlign: isRTL ? 'right' : 'left', mb: 2, lineHeight: 1.8, color: 'text.secondary' }}
      >
        {t('term_condition_para1')}
      </Typography>

      <Typography
        variant="h6"
        sx={{ fontWeight: 'bold', textAlign: isRTL ? 'right' : 'left', mt: 2, mb: 1, color: 'text.primary' }}
      >
        {t('term_condition_heading1')}
      </Typography>

      <Typography
        variant="body1"
        paragraph
        sx={{ textAlign: isRTL ? 'right' : 'left', mb: 2, lineHeight: 1.8, color: 'text.secondary' }}
      >
        {t('term_condition_para2')}
      </Typography>

      <Typography
        variant="h6"
        sx={{ fontWeight: 'bold', textAlign: isRTL ? 'right' : 'left', mt: 2, mb: 1, color: 'text.primary' }}
      >
        {t('term_condition_heading2')}
      </Typography>

      <Typography
        variant="body1"
        paragraph
        sx={{ textAlign: isRTL ? 'right' : 'left', mb: 2, lineHeight: 1.8, color: 'text.secondary' }}
      >
        {t('term_condition_para3')}
      </Typography>

      <Typography
        variant="h6"
        sx={{ fontWeight: 'bold', textAlign: isRTL ? 'right' : 'left', mt: 2, mb: 1, color: 'text.primary' }}
      >
        {t('term_condition_heading3')}
      </Typography>

      <Typography
        variant="body1"
        paragraph
        sx={{ textAlign: isRTL ? 'right' : 'left', mb: 2, lineHeight: 1.8, color: 'text.secondary' }}
      >
        {t('term_condition_para4')}
      </Typography>

      <Typography
        variant="h6"
        sx={{ fontWeight: 'bold', textAlign: isRTL ? 'right' : 'left', mt: 2, mb: 1, color: 'text.primary' }}
      >
        {t('term_condition_heading4')}
      </Typography>

      <Typography
        variant="body1"
        paragraph
        sx={{ textAlign: isRTL ? 'right' : 'left', mb: 2, lineHeight: 1.8, color: 'text.secondary' }}
      >
        {t('term_condition_para5')}
      </Typography>

      <Typography
        variant="h6"
        sx={{ fontWeight: 'bold', textAlign: isRTL ? 'right' : 'left', mt: 2, mb: 1, color: 'text.primary' }}
      >
        {t('term_condition_heading5')}
      </Typography>

      <Typography
        variant="body1"
        paragraph
        sx={{ textAlign: isRTL ? 'right' : 'left', mb: 2, lineHeight: 1.8, color: 'text.secondary' }}
      >
        {t('term_condition_para6')}
      </Typography>

      <Typography
        variant="h6"
        sx={{ fontWeight: 'bold', textAlign: isRTL ? 'right' : 'left', mt: 2, mb: 1, color: 'text.primary' }}
      >
        {t('term_condition_heading6')}
      </Typography>

      <Typography
        variant="body1"
        paragraph
        sx={{ textAlign: isRTL ? 'right' : 'left', mb: 2, lineHeight: 1.8, color: 'text.secondary' }}
      >
        {t('term_condition_para7')}
      </Typography>

      <Typography
        variant="h6"
        sx={{ fontWeight: 'bold', textAlign: isRTL ? 'right' : 'left', mt: 2, mb: 1, color: 'text.primary' }}
      >
        {t('term_condition_heading7')}
      </Typography>

      <Typography
        variant="body1"
        paragraph
        sx={{ textAlign: isRTL ? 'right' : 'left', mb: 2, lineHeight: 1.8, color: 'text.secondary' }}
      >
        {t('term_condition_para8')}
      </Typography>

      <Typography
        variant="h6"
        sx={{ fontWeight: 'bold', textAlign: isRTL ? 'right' : 'left', mt: 2, mb: 1, color: 'text.primary' }}
      >
        {t('term_condition_heading8')}
      </Typography>

      <Typography
        variant="body1"
        paragraph
        sx={{ textAlign: isRTL ? 'right' : 'left', mb: 2, lineHeight: 1.8, color: 'text.secondary' }}
      >
        {t('term_condition_para9')}
      </Typography>

      <Typography
        variant="h6"
        sx={{ fontWeight: 'bold', textAlign: isRTL ? 'right' : 'left', mt: 2, mb: 1, color: 'text.primary' }}
      >
        {t('term_condition_heading9')}
      </Typography>

      <Typography
        variant="body1"
        paragraph
        sx={{ textAlign: isRTL ? 'right' : 'left', mb: 2, lineHeight: 1.8, color: 'text.secondary' }}
      >
        {t('term_condition_para10')}
      </Typography>

      <Typography
        variant="h6"
        sx={{ fontWeight: 'bold', textAlign: isRTL ? 'right' : 'left', mt: 2, mb: 1, color: 'text.primary' }}
      >
        {t('term_condition_heading10')}
      </Typography>

      <Typography
        variant="body1"
        paragraph
        sx={{ textAlign: isRTL ? 'right' : 'left', mb: 2, lineHeight: 1.8, color: 'text.secondary' }}
      >
        {t('term_condition_para11')}
      </Typography>

      <Typography
        variant="h6"
        sx={{ fontWeight: 'bold', textAlign: isRTL ? 'right' : 'left', mt: 2, mb: 1, color: 'text.primary' }}
      >
        {t('term_condition_heading11')}
      </Typography>

      <Typography
        variant="body1"
        paragraph
        sx={{ textAlign: isRTL ? 'right' : 'left', mb: 2, lineHeight: 1.8, color: 'text.secondary' }}
      >
        {t('term_condition_para12')}
      </Typography>

      <Typography
        variant="h6"
        sx={{ fontWeight: 'bold', textAlign: isRTL ? 'right' : 'left', mt: 2, mb: 1, color: 'text.primary' }}
      >
        {t('term_condition_heading12')}
      </Typography>

      <Typography
        variant="body1"
        paragraph
        sx={{ textAlign: isRTL ? 'right' : 'left', mb: 2, lineHeight: 1.8, color: 'text.secondary' }}
      >
        {t('term_condition_para13')}
      </Typography>

      <Typography
        variant="body1"
        paragraph
        sx={{ textAlign: isRTL ? 'right' : 'left', mb: 2, lineHeight: 1.8, color: 'text.secondary' }}
      >
        {t('term_condition_para14')}
        {settings && settings.contact_email && (
          <Button
            component="a"
            href={"mailto:" + settings.contact_email}
            sx={{ ml: 0.5, fontWeight: 'bold', textTransform: 'none', p: 0, minWidth: 0 }}
          >
            {settings.contact_email}
          </Button>
        )}
      </Typography>
    </Box>
  );
}